/**
 * Session Focus Picker
 *
 * Inline welcome card for extraction sessions. Renders inside the chat
 * messages area as a first message, not a modal gate. User can start
 * typing without picking a focus.
 */

import { useState } from 'preact/hooks';
import type { SessionFocus } from '../api/client';

interface Gap {
	id: string;
	section: string;
	field?: string;
	reason: string;
	priority: 'high' | 'medium' | 'low';
	status: 'pending' | 'explored' | 'resolved';
	category?: 'new_content' | 'refinement';
}

interface Props {
	gaps: Gap[];
	onFocusSelected: (focus: SessionFocus) => void;
	loading: boolean;
	seedUrls: string;
	onSeedUrlsChange: (urls: string) => void;
}

/**
 * Extract entity-level refinement suggestions from gaps.
 * Groups by entity name (company or project) and picks the top 3.
 */
function getRefineSuggestions(gaps: Gap[]): Array<{ label: string; section: string }> {
	const entityMap = new Map<string, { section: string; count: number; priority: number }>();

	for (const gap of gaps) {
		if (gap.category !== 'refinement' && gap.category !== undefined) continue;

		// Extract entity name from reason text
		const match = gap.reason.match(/for (.+?)(?:\s*[—–-]\s*|\s*\(|$)/);
		if (!match) continue;

		const name = match[1].replace(/["']/g, '').trim();
		const existing = entityMap.get(name);
		const priorityScore = gap.priority === 'high' ? 3 : gap.priority === 'medium' ? 2 : 1;

		if (!existing || priorityScore > existing.priority) {
			entityMap.set(name, {
				section: gap.section,
				count: (existing?.count || 0) + 1,
				priority: Math.max(existing?.priority || 0, priorityScore),
			});
		} else {
			existing.count++;
		}
	}

	return [...entityMap.entries()]
		.sort((a, b) => b[1].priority - a[1].priority || b[1].count - a[1].count)
		.slice(0, 3)
		.map(([label, { section }]) => ({ label, section }));
}

export function SessionFocusPicker({ gaps, onFocusSelected, loading, seedUrls, onSeedUrlsChange }: Props) {
	const [showUrls, setShowUrls] = useState(false);

	const refineSuggestions = getRefineSuggestions(gaps);

	if (loading) {
		return (
			<div class="focus-card focus-card--loading">
				<div class="focus-loading">
					<div class="focus-loading-spinner">
						<span /><span /><span />
					</div>
					<p class="focus-loading-text">Generating your opening...</p>
				</div>
				<style>{focusPickerStyles}</style>
			</div>
		);
	}

	// Identify uncovered areas from gaps for "Add new" suggestions
	const newContentGaps = gaps
		.filter(g => g.category === 'new_content' && g.priority !== 'low')
		.slice(0, 2);

	const hasOptions = newContentGaps.length > 0 || refineSuggestions.length > 0;

	return (
		<div class="focus-card">
			<h3 class="focus-title">{hasOptions ? 'Pick up where we left off' : "Let's build your profile"}</h3>
			{hasOptions && <p class="focus-subtitle">or just start typing about anything</p>}

			<div class="focus-options">
				{newContentGaps.length > 0 ? (
					newContentGaps.map((gap, i) => {
						const label = gap.reason.replace(/^Missing\s+/i, '').replace(/\s*[—–-].*$/, '');
						return (
							<button
								key={i}
								class="focus-option focus-option--new"
								onClick={() => onFocusSelected({ type: 'new_content', topic: label })}
							>
								<span class="focus-option-tag">new</span>
								<span class="focus-option-label">{label}</span>
							</button>
						);
					})
				) : (
					<button
						class="focus-option focus-option--new"
						onClick={() => onFocusSelected({ type: 'new_content', topic: '' })}
					>
						<span class="focus-option-label">Something new</span>
					</button>
				)}

				{refineSuggestions.map(({ label, section }) => (
					<button
						key={section}
						class="focus-option focus-option--refine"
						onClick={() => onFocusSelected({ type: 'refine', target: label, targetSection: section })}
					>
						<span class="focus-option-tag">continue</span>
						<span class="focus-option-label">{label}</span>
					</button>
				))}

				<button
					class="focus-option focus-option--guided"
					onClick={() => onFocusSelected({ type: 'guided' })}
				>
					<span class="focus-option-label">Guide me</span>
				</button>

				<button
					class="focus-option focus-option--refine"
					onClick={() => onFocusSelected({ type: 'refine' })}
				>
					<span class="focus-option-tag">refine</span>
					<span class="focus-option-label">Refine existing record</span>
				</button>
			</div>

			<button
				class="focus-url-toggle"
				onClick={() => setShowUrls(!showUrls)}
			>
				<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ transform: showUrls ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}>
					<path d="M9 18l6-6-6-6" />
				</svg>
				Have links to share?
			</button>

			{showUrls && (
				<textarea
					class="focus-url-input"
					value={seedUrls}
					onInput={(e) => onSeedUrlsChange((e.target as HTMLTextAreaElement).value)}
					placeholder="Paste portfolio, resume, or project URLs (one per line)"
					rows={3}
				/>
			)}

			<style>{focusPickerStyles}</style>
		</div>
	);
}

const focusPickerStyles = `
	.focus-card {
		max-width: 480px;
		padding: 1.25rem 1.5rem;
		margin: 0 auto 1rem;
	}

	.focus-card--loading {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 2rem;
	}

	.focus-title {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0;
	}

	.focus-subtitle {
		font-size: 0.8125rem;
		color: var(--color-text-subtle);
		margin: 0.25rem 0 0;
	}

	.focus-options {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin: 1rem 0;
	}

	.focus-option {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.875rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		cursor: pointer;
		transition: border-color 200ms var(--ease-out-quart);
		text-align: left;
	}

	.focus-option:hover {
		border-color: var(--color-text);
	}

	.focus-option-tag {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		padding: 0.125rem 0.375rem;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		flex-shrink: 0;
	}

	.focus-option--new:hover .focus-option-tag {
		border-color: var(--color-primary);
		color: var(--color-primary);
	}

	.focus-option--refine:hover .focus-option-tag {
		border-color: var(--color-text-muted);
		color: var(--color-text-muted);
	}

	.focus-option--correct:hover .focus-option-tag {
		border-color: var(--color-warning, #b07d1e);
		color: var(--color-warning, #b07d1e);
	}

	.focus-option-label {
		font-family: var(--font-body);
		font-size: 0.8125rem;
		font-weight: 500;
		color: var(--color-text);
		line-height: 1.3;
	}

	.focus-url-toggle {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		background: none;
		border: none;
		padding: 0;
		font-family: var(--font-body);
		font-size: 0.75rem;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: color 150ms;
	}

	.focus-url-toggle:hover {
		color: var(--color-text);
	}

	.focus-url-input {
		width: 100%;
		margin-top: 0.5rem;
		padding: 0.625rem 0.75rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		font-family: var(--font-body);
		font-size: 0.8125rem;
		color: var(--color-text);
		resize: vertical;
		outline: none;
		transition: border-color 200ms;
	}

	.focus-url-input:focus {
		border-color: var(--color-text);
	}

	.focus-url-input::placeholder {
		color: var(--color-text-subtle);
	}

	.focus-loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
	}

	.focus-loading-spinner {
		display: flex;
		gap: 4px;
	}

	.focus-loading-spinner span {
		width: 8px;
		height: 8px;
		background: var(--color-text-muted);
		border-radius: 50%;
		animation: chat-typing 1.4s infinite;
	}

	.focus-loading-spinner span:nth-child(2) {
		animation-delay: 0.2s;
	}

	.focus-loading-spinner span:nth-child(3) {
		animation-delay: 0.4s;
	}

	.focus-loading-text {
		font-size: 0.875rem;
		color: var(--color-text-muted);
		margin: 0;
	}

	@media (max-width: 480px) {
		.focus-card {
			padding: 1rem;
		}
	}
`;
